import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const LanguageContext = createContext();

export const SUPPORTED_LANGUAGES = ['en', 'fr', 'de', 'es'];
export const DEFAULT_LANGUAGE = 'en';

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract language from URL path
  const getLanguageFromPath = () => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const potentialLang = pathParts[0];
    return SUPPORTED_LANGUAGES.includes(potentialLang) ? potentialLang : DEFAULT_LANGUAGE;
  };

  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Get language from URL first, then localStorage, then default
    const urlLang = getLanguageFromPath();
    if (urlLang !== DEFAULT_LANGUAGE) return urlLang;
    return localStorage.getItem('language') || DEFAULT_LANGUAGE;
  });

  useEffect(() => {
    // Save language preference
    localStorage.setItem('language', currentLanguage);
  }, [currentLanguage]);

  useEffect(() => {
    // Update language when URL changes
    const urlLang = getLanguageFromPath();
    if (urlLang !== currentLanguage) {
      setCurrentLanguage(urlLang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const changeLanguage = (lang) => {
    if (!SUPPORTED_LANGUAGES.includes(lang)) return;
    
    setCurrentLanguage(lang);
    localStorage.setItem('language', lang);
    
    // Update URL path
    const pathParts = location.pathname.split('/').filter(Boolean);
    const currentPathLang = SUPPORTED_LANGUAGES.includes(pathParts[0]) ? pathParts[0] : null;
    
    let newPath;
    if (currentPathLang) {
      // Replace existing language
      pathParts[0] = lang;
      newPath = '/' + pathParts.join('/');
    } else {
      // Add language prefix
      newPath = lang === DEFAULT_LANGUAGE ? location.pathname : `/${lang}${location.pathname}`;
    }
    
    // Remove language prefix if switching to default language
    if (lang === DEFAULT_LANGUAGE && currentPathLang) {
      pathParts.shift();
      newPath = '/' + pathParts.join('/');
    }
    
    navigate(newPath, { replace: true });
  };

  const getLocalizedPath = (path) => {
    if (currentLanguage === DEFAULT_LANGUAGE) return path;
    return `/${currentLanguage}${path}`;
  };

  const value = {
    currentLanguage,
    changeLanguage,
    getLocalizedPath,
    t: (key) => translations[currentLanguage]?.[key] || translations[DEFAULT_LANGUAGE][key] || key
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Translations object
const translations = {
  en: {
    // SEO & Meta
    'meta.title': 'Instant-Email - Free Temporary Disposable Email Address',
    'meta.description': 'Get instant temporary email addresses for free. Protect your privacy with disposable emails that auto-delete after 24 hours. No registration required. Anonymous and secure.',
    'meta.keywords': 'temporary email, disposable email, temp mail, fake email, anonymous email, throwaway email, instant email, email privacy',
    
    // Navigation
    'nav.login': 'Login',
    'nav.signup': 'Sign Up',
    'nav.dashboard': 'Go to Dashboard',
    'nav.backToHome': 'Back to Home',
    'nav.logout': 'Logout',
    
    // Auth Pages
    'auth.login.title': 'Welcome Back',
    'auth.login.subtitle': 'Sign in to Instant-Email',
    'auth.login.email': 'Email',
    'auth.login.password': 'Password',
    'auth.login.submit': 'Sign In',
    'auth.login.signingIn': 'Signing in...',
    'auth.login.noAccount': "Don't have an account?",
    'auth.login.createOne': 'Create one',
    'auth.login.success': 'Welcome back!',
    'auth.login.emailReady': 'Email address ready!',
    'auth.login.emailWillBeAdded': 'will be added to your account.',
    'auth.register.title': 'Create Account',
    'auth.register.subtitle': 'Join Instant-Email for free',
    'auth.register.username': 'Username',
    'auth.register.email': 'Email',
    'auth.register.password': 'Password',
    'auth.register.passwordHint': 'Minimum 6 characters',
    'auth.register.submit': 'Create Account',
    'auth.register.creating': 'Creating account...',
    'auth.register.haveAccount': 'Already have an account?',
    'auth.register.signIn': 'Sign in',
    'auth.register.success': 'Account created! Welcome to Instant-Email',
    'auth.fillAllFields': 'Please fill in all fields',
    'auth.passwordTooShort': 'Password must be at least 6 characters',
    
    // Hero Section
    'hero.badge': 'Instant Disposable Email',
    'hero.title': 'Your Temporary Email',
    'hero.titleHighlight': 'Ready to Use',
    'hero.subtitle': 'Use this email address anywhere. Incoming emails appear below instantly.',
    
    // Email Actions
    'email.copy': 'Email address copied!',
    'email.refresh': 'Refresh',
    'email.addToAccount': 'Add to My Account',
    'email.saveAddress': 'Save This Address',
    'email.addTip': 'Add this address to your dashboard',
    'email.saveTip': 'Register to keep this address permanently',
    'email.quickTip': 'Click the email to copy • Auto-refreshes every 10 seconds',
    'email.newGenerated': 'New email address generated!',
    'email.create': 'Create Email Address',
    'email.myAddresses': 'My Email Addresses',
    'email.noAddresses': 'No email addresses yet',
    'email.createFirst': 'Create your first email address to get started',
    
    // Inbox
    'inbox.title': 'Inbox',
    'inbox.new': 'new',
    'inbox.empty': 'No messages yet',
    'inbox.emptyDesc': 'Emails sent to this address will appear here',
    'inbox.selectEmail': 'Select an email to read',
    'inbox.refreshed': 'Inbox refreshed',
    
    // Dashboard
    'dashboard.title': 'Dashboard',
    'dashboard.welcome': 'Welcome back',
    'dashboard.createNew': 'Create New Address',
    'dashboard.username': 'Username',
    'dashboard.domain': 'Domain',
    'dashboard.selectDomain': 'Select a domain',
    'dashboard.expires': 'Expires',
    'dashboard.created': 'Created',
    'dashboard.delete': 'Delete',
    'dashboard.addressCreated': 'Email address created!',
    'dashboard.addressDeleted': 'Address deleted',
    'dashboard.messageDeleted': 'Message deleted',
    
    // Features
    'feature.privacy.title': 'Privacy First',
    'feature.privacy.desc': 'Your real email stays hidden. No spam, no tracking.',
    'feature.instant.title': 'Instant Creation',
    'feature.instant.desc': 'Generate a disposable email in seconds.',
    'feature.anonymous.title': 'Anonymous',
    'feature.anonymous.desc': 'No personal data stored. Complete privacy.',
    'feature.autodelete.title': '24h Auto-Delete',
    'feature.autodelete.desc': 'Emails automatically expire after 24 hours.',
    
    // CTA Section
    'cta.title': 'Want More Features?',
    'cta.description': 'Create a free account for custom usernames, multiple addresses, and message management.',
    'cta.button': 'Create Free Account',
    
    // Footer Links
    'footer.about': 'About',
    'footer.faq': 'FAQ',
    'footer.contact': 'Contact',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.copyright': 'All rights reserved',
    'footer.tagline': 'Made with ❤️ for your privacy',
    
    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.close': 'Close',
  },
  fr: {
    // SEO & Meta
    'meta.title': 'Instant-Email - Adresse Email Temporaire Jetable Gratuite',
    'meta.description': 'Obtenez des adresses email temporaires gratuites instantanément. Protégez votre vie privée avec des emails jetables qui se suppriment automatiquement après 24 heures. Sans inscription. Anonyme et sécurisé.',
    'meta.keywords': 'email temporaire, email jetable, temp mail, faux email, email anonyme, email éphémère, email instantané, confidentialité email',
    
    // Navigation
    'nav.login': 'Connexion',
    'nav.signup': "S'inscrire",
    'nav.dashboard': 'Tableau de bord',
    'nav.backToHome': "Retour à l'accueil",
    'nav.logout': 'Déconnexion',
    
    // Auth Pages
    'auth.login.title': 'Bon Retour',
    'auth.login.subtitle': 'Connectez-vous à Instant-Email',
    'auth.login.email': 'Email',
    'auth.login.password': 'Mot de passe',
    'auth.login.submit': 'Se Connecter',
    'auth.login.signingIn': 'Connexion...',
    'auth.login.noAccount': "Vous n'avez pas de compte?",
    'auth.login.createOne': 'Créer un compte',
    'auth.login.success': 'Bon retour!',
    'auth.login.emailReady': 'Adresse email prête!',
    'auth.login.emailWillBeAdded': 'sera ajoutée à votre compte.',
    'auth.register.title': 'Créer un Compte',
    'auth.register.subtitle': 'Rejoignez Instant-Email gratuitement',
    'auth.register.username': "Nom d'utilisateur",
    'auth.register.email': 'Email',
    'auth.register.password': 'Mot de passe',
    'auth.register.passwordHint': 'Minimum 6 caractères',
    'auth.register.submit': 'Créer un Compte',
    'auth.register.creating': 'Création du compte...',
    'auth.register.haveAccount': 'Vous avez déjà un compte?',
    'auth.register.signIn': 'Se connecter',
    'auth.register.success': 'Compte créé! Bienvenue sur Instant-Email',
    'auth.fillAllFields': 'Veuillez remplir tous les champs',
    'auth.passwordTooShort': 'Le mot de passe doit contenir au moins 6 caractères',
    
    // Hero Section
    'hero.badge': 'Email Jetable Instantané',
    'hero.title': 'Votre Email Temporaire',
    'hero.titleHighlight': 'Prêt à Utiliser',
    'hero.subtitle': 'Utilisez cette adresse email partout. Les emails entrants apparaissent instantanément ci-dessous.',
    
    // Email Actions
    'email.copy': 'Adresse email copiée!',
    'email.refresh': 'Actualiser',
    'email.addToAccount': 'Ajouter à mon compte',
    'email.saveAddress': 'Enregistrer cette adresse',
    'email.addTip': 'Ajoutez cette adresse à votre tableau de bord',
    'email.saveTip': 'Inscrivez-vous pour conserver cette adresse en permanence',
    'email.quickTip': "Cliquez sur l'email pour copier • Actualisation automatique toutes les 10 secondes",
    'email.newGenerated': 'Nouvelle adresse email générée!',
    'email.create': 'Créer une adresse email',
    'email.myAddresses': 'Mes adresses email',
    'email.noAddresses': 'Aucune adresse email pour le moment',
    'email.createFirst': 'Créez votre première adresse email pour commencer',
    
    // Inbox
    'inbox.title': 'Boîte de réception',
    'inbox.new': 'nouveau',
    'inbox.empty': 'Aucun message pour le moment',
    'inbox.emptyDesc': 'Les emails envoyés à cette adresse apparaîtront ici',
    'inbox.selectEmail': 'Sélectionnez un email à lire',
    'inbox.refreshed': 'Boîte de réception actualisée',
    
    // Dashboard
    'dashboard.title': 'Tableau de bord',
    'dashboard.welcome': 'Bon retour',
    'dashboard.createNew': 'Créer une nouvelle adresse',
    'dashboard.username': "Nom d'utilisateur",
    'dashboard.domain': 'Domaine',
    'dashboard.selectDomain': 'Sélectionnez un domaine',
    'dashboard.expires': 'Expire',
    'dashboard.created': 'Créé',
    'dashboard.delete': 'Supprimer',
    'dashboard.addressCreated': 'Adresse email créée!',
    'dashboard.addressDeleted': 'Adresse supprimée',
    'dashboard.messageDeleted': 'Message supprimé',
    
    // Features
    'feature.privacy.title': 'Confidentialité Avant Tout',
    'feature.privacy.desc': 'Votre vrai email reste caché. Pas de spam, pas de suivi.',
    'feature.instant.title': 'Création Instantanée',
    'feature.instant.desc': 'Générez un email jetable en quelques secondes.',
    'feature.anonymous.title': 'Anonyme',
    'feature.anonymous.desc': 'Aucune donnée personnelle stockée. Confidentialité totale.',
    'feature.autodelete.title': 'Suppression Auto 24h',
    'feature.autodelete.desc': 'Les emails expirent automatiquement après 24 heures.',
    
    // CTA Section
    'cta.title': 'Vous voulez plus de fonctionnalités?',
    'cta.description': 'Créez un compte gratuit pour des noms d\'utilisateur personnalisés, plusieurs adresses et la gestion des messages.',
    'cta.button': 'Créer un Compte Gratuit',
    
    // Footer Links
    'footer.about': 'À propos',
    'footer.faq': 'FAQ',
    'footer.contact': 'Contact',
    'footer.privacy': 'Politique de confidentialité',
    'footer.terms': 'Conditions de service',
    'footer.copyright': 'Tous droits réservés',
    'footer.tagline': 'Fait avec ❤️ pour votre vie privée',
    
    // Common
    'common.loading': 'Chargement...',
    'common.error': 'Erreur',
    'common.success': 'Succès',
    'common.cancel': 'Annuler',
    'common.save': 'Enregistrer',
    'common.close': 'Fermer',
  },
  de: {
    // SEO & Meta
    'meta.title': 'Instant-Email - Kostenlose Temporäre Wegwerf-E-Mail-Adresse',
    'meta.description': 'Erhalten Sie sofort kostenlose temporäre E-Mail-Adressen. Schützen Sie Ihre Privatsphäre mit Wegwerf-E-Mails, die nach 24 Stunden automatisch gelöscht werden. Keine Registrierung erforderlich. Anonym und sicher.',
    'meta.keywords': 'temporäre email, wegwerf email, temp mail, fake email, anonyme email, einweg email, sofort email, email privatsphäre',
    
    // Navigation
    'nav.login': 'Anmelden',
    'nav.signup': 'Registrieren',
    'nav.dashboard': 'Zum Dashboard',
    'nav.backToHome': 'Zurück zur Startseite',
    'nav.logout': 'Abmelden',
    
    // Auth Pages
    'auth.login.title': 'Willkommen zurück',
    'auth.login.subtitle': 'Bei Instant-Email anmelden',
    'auth.login.email': 'E-Mail',
    'auth.login.password': 'Passwort',
    'auth.login.submit': 'Anmelden',
    'auth.login.signingIn': 'Anmeldung...',
    'auth.login.noAccount': 'Noch kein Konto?',
    'auth.login.createOne': 'Konto erstellen',
    'auth.login.success': 'Willkommen zurück!',
    'auth.login.emailReady': 'E-Mail-Adresse bereit!',
    'auth.login.emailWillBeAdded': 'wird zu Ihrem Konto hinzugefügt.',
    'auth.register.title': 'Konto Erstellen',
    'auth.register.subtitle': 'Kostenlos bei Instant-Email anmelden',
    'auth.register.username': 'Benutzername',
    'auth.register.email': 'E-Mail',
    'auth.register.password': 'Passwort',
    'auth.register.passwordHint': 'Mindestens 6 Zeichen',
    'auth.register.submit': 'Konto Erstellen',
    'auth.register.creating': 'Konto wird erstellt...',
    'auth.register.haveAccount': 'Haben Sie bereits ein Konto?',
    'auth.register.signIn': 'Anmelden',
    'auth.register.success': 'Konto erstellt! Willkommen bei Instant-Email',
    'auth.fillAllFields': 'Bitte füllen Sie alle Felder aus',
    'auth.passwordTooShort': 'Das Passwort muss mindestens 6 Zeichen lang sein',
    
    // Hero Section
    'hero.badge': 'Sofortige Wegwerf-E-Mail',
    'hero.title': 'Ihre temporäre E-Mail',
    'hero.titleHighlight': 'Einsatzbereit',
    'hero.subtitle': 'Verwenden Sie diese E-Mail-Adresse überall. Eingehende E-Mails erscheinen sofort unten.',
    
    // Email Actions
    'email.copy': 'E-Mail-Adresse kopiert!',
    'email.refresh': 'Aktualisieren',
    'email.addToAccount': 'Zu meinem Konto hinzufügen',
    'email.saveAddress': 'Diese Adresse speichern',
    'email.addTip': 'Fügen Sie diese Adresse zu Ihrem Dashboard hinzu',
    'email.saveTip': 'Registrieren Sie sich, um diese Adresse dauerhaft zu behalten',
    'email.quickTip': 'Klicken Sie auf die E-Mail zum Kopieren • Automatische Aktualisierung alle 10 Sekunden',
    'email.newGenerated': 'Neue E-Mail-Adresse generiert!',
    'email.create': 'E-Mail-Adresse erstellen',
    'email.myAddresses': 'Meine E-Mail-Adressen',
    'email.noAddresses': 'Noch keine E-Mail-Adressen',
    'email.createFirst': 'Erstellen Sie Ihre erste E-Mail-Adresse, um zu beginnen',
    
    // Inbox
    'inbox.title': 'Posteingang',
    'inbox.new': 'neu',
    'inbox.empty': 'Noch keine Nachrichten',
    'inbox.emptyDesc': 'An diese Adresse gesendete E-Mails erscheinen hier',
    'inbox.selectEmail': 'Wählen Sie eine E-Mail zum Lesen aus',
    'inbox.refreshed': 'Posteingang aktualisiert',
    
    // Dashboard
    'dashboard.title': 'Dashboard',
    'dashboard.welcome': 'Willkommen zurück',
    'dashboard.createNew': 'Neue Adresse erstellen',
    'dashboard.username': 'Benutzername',
    'dashboard.domain': 'Domain',
    'dashboard.selectDomain': 'Wählen Sie eine Domain',
    'dashboard.expires': 'Läuft ab',
    'dashboard.created': 'Erstellt',
    'dashboard.delete': 'Löschen',
    'dashboard.addressCreated': 'E-Mail-Adresse erstellt!',
    'dashboard.addressDeleted': 'Adresse gelöscht',
    'dashboard.messageDeleted': 'Nachricht gelöscht',
    
    // Features
    'feature.privacy.title': 'Datenschutz zuerst',
    'feature.privacy.desc': 'Ihre echte E-Mail bleibt verborgen. Kein Spam, kein Tracking.',
    'feature.instant.title': 'Sofortige Erstellung',
    'feature.instant.desc': 'Erstellen Sie eine Wegwerf-E-Mail in Sekunden.',
    'feature.anonymous.title': 'Anonym',
    'feature.anonymous.desc': 'Keine persönlichen Daten gespeichert. Vollständige Privatsphäre.',
    'feature.autodelete.title': '24h Auto-Löschung',
    'feature.autodelete.desc': 'E-Mails laufen automatisch nach 24 Stunden ab.',
    
    // CTA Section
    'cta.title': 'Möchten Sie mehr Funktionen?',
    'cta.description': 'Erstellen Sie ein kostenloses Konto für benutzerdefinierte Benutzernamen, mehrere Adressen und Nachrichtenverwaltung.',
    'cta.button': 'Kostenloses Konto Erstellen',
    
    // Footer Links
    'footer.about': 'Über uns',
    'footer.faq': 'FAQ',
    'footer.contact': 'Kontakt',
    'footer.privacy': 'Datenschutzrichtlinie',
    'footer.terms': 'Nutzungsbedingungen',
    'footer.copyright': 'Alle Rechte vorbehalten',
    'footer.tagline': 'Mit ❤️ für Ihre Privatsphäre gemacht',
    
    // Common
    'common.loading': 'Laden...',
    'common.error': 'Fehler',
    'common.success': 'Erfolg',
    'common.cancel': 'Abbrechen',
    'common.save': 'Speichern',
    'common.close': 'Schließen',
  },
  es: {
    // SEO & Meta
    'meta.title': 'Instant-Email - Dirección de Correo Electrónico Temporal Desechable Gratis',
    'meta.description': 'Obtén direcciones de correo electrónico temporales gratis al instante. Protege tu privacidad con correos desechables que se auto-eliminan después de 24 horas. Sin registro requerido. Anónimo y seguro.',
    'meta.keywords': 'email temporal, correo desechable, temp mail, email falso, correo anónimo, email descartable, correo instantáneo, privacidad email',
    
    // Navigation
    'nav.login': 'Iniciar sesión',
    'nav.signup': 'Registrarse',
    'nav.dashboard': 'Ir al Panel',
    'nav.backToHome': 'Volver al Inicio',
    'nav.logout': 'Cerrar sesión',
    
    // Auth Pages
    'auth.login.title': 'Bienvenido de Nuevo',
    'auth.login.subtitle': 'Inicia sesión en Instant-Email',
    'auth.login.email': 'Correo electrónico',
    'auth.login.password': 'Contraseña',
    'auth.login.submit': 'Iniciar Sesión',
    'auth.login.signingIn': 'Iniciando sesión...',
    'auth.login.noAccount': '¿No tienes una cuenta?',
    'auth.login.createOne': 'Crear una',
    'auth.login.success': '¡Bienvenido de nuevo!',
    'auth.login.emailReady': '¡Dirección de correo lista!',
    'auth.login.emailWillBeAdded': 'se agregará a tu cuenta.',
    'auth.register.title': 'Crear Cuenta',
    'auth.register.subtitle': 'Únete a Instant-Email gratis',
    'auth.register.username': 'Nombre de usuario',
    'auth.register.email': 'Correo electrónico',
    'auth.register.password': 'Contraseña',
    'auth.register.passwordHint': 'Mínimo 6 caracteres',
    'auth.register.submit': 'Crear Cuenta',
    'auth.register.creating': 'Creando cuenta...',
    'auth.register.haveAccount': '¿Ya tienes una cuenta?',
    'auth.register.signIn': 'Iniciar sesión',
    'auth.register.success': '¡Cuenta creada! Bienvenido a Instant-Email',
    'auth.fillAllFields': 'Por favor completa todos los campos',
    'auth.passwordTooShort': 'La contraseña debe tener al menos 6 caracteres',
    
    // Hero Section
    'hero.badge': 'Email Desechable Instantáneo',
    'hero.title': 'Tu Email Temporal',
    'hero.titleHighlight': 'Listo para Usar',
    'hero.subtitle': 'Usa esta dirección de correo en cualquier lugar. Los correos entrantes aparecen instantáneamente abajo.',
    
    // Email Actions
    'email.copy': '¡Dirección de correo copiada!',
    'email.refresh': 'Actualizar',
    'email.addToAccount': 'Agregar a mi cuenta',
    'email.saveAddress': 'Guardar esta dirección',
    'email.addTip': 'Agrega esta dirección a tu panel',
    'email.saveTip': 'Regístrate para mantener esta dirección permanentemente',
    'email.quickTip': 'Haz clic en el correo para copiar • Actualización automática cada 10 segundos',
    'email.newGenerated': '¡Nueva dirección de correo generada!',
    'email.create': 'Crear dirección de correo',
    'email.myAddresses': 'Mis direcciones de correo',
    'email.noAddresses': 'Aún no hay direcciones de correo',
    'email.createFirst': 'Crea tu primera dirección de correo para comenzar',
    
    // Inbox
    'inbox.title': 'Bandeja de entrada',
    'inbox.new': 'nuevo',
    'inbox.empty': 'Aún no hay mensajes',
    'inbox.emptyDesc': 'Los correos enviados a esta dirección aparecerán aquí',
    'inbox.selectEmail': 'Selecciona un correo para leer',
    'inbox.refreshed': 'Bandeja de entrada actualizada',
    
    // Dashboard
    'dashboard.title': 'Panel de Control',
    'dashboard.welcome': 'Bienvenido de nuevo',
    'dashboard.createNew': 'Crear nueva dirección',
    'dashboard.username': 'Nombre de usuario',
    'dashboard.domain': 'Dominio',
    'dashboard.selectDomain': 'Selecciona un dominio',
    'dashboard.expires': 'Expira',
    'dashboard.created': 'Creado',
    'dashboard.delete': 'Eliminar',
    'dashboard.addressCreated': '¡Dirección de correo creada!',
    'dashboard.addressDeleted': 'Dirección eliminada',
    'dashboard.messageDeleted': 'Mensaje eliminado',
    
    // Features
    'feature.privacy.title': 'Privacidad Primero',
    'feature.privacy.desc': 'Tu correo real permanece oculto. Sin spam, sin rastreo.',
    'feature.instant.title': 'Creación Instantánea',
    'feature.instant.desc': 'Genera un correo desechable en segundos.',
    'feature.anonymous.title': 'Anónimo',
    'feature.anonymous.desc': 'Sin datos personales almacenados. Privacidad completa.',
    'feature.autodelete.title': 'Auto-eliminación 24h',
    'feature.autodelete.desc': 'Los correos expiran automáticamente después de 24 horas.',
    
    // CTA Section
    'cta.title': '¿Quieres más funciones?',
    'cta.description': 'Crea una cuenta gratuita para nombres de usuario personalizados, múltiples direcciones y gestión de mensajes.',
    'cta.button': 'Crear Cuenta Gratuita',
    
    // Footer Links
    'footer.about': 'Acerca de',
    'footer.faq': 'Preguntas frecuentes',
    'footer.contact': 'Contacto',
    'footer.privacy': 'Política de Privacidad',
    'footer.terms': 'Términos de Servicio',
    'footer.copyright': 'Todos los derechos reservados',
    'footer.tagline': 'Hecho con ❤️ para tu privacidad',
    
    // Common
    'common.loading': 'Cargando...',
    'common.error': 'Error',
    'common.success': 'Éxito',
    'common.cancel': 'Cancelar',
    'common.save': 'Guardar',
    'common.close': 'Cerrar',
  }
};
